import { ProjectType } from '@api';
import {
  CompanyAnchor,
  formatDate,
  formatNumber,
  formatPercent,
  PersonAnchor,
  ShowTableCell,
} from '@common';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'src/common/utils/formatCurrency';
import { Arrangement } from './components';

const TRANS_PATH = 'PROJECTS_TABLE.COLUMNS';

type UseProjectsTableColumnsReturnType = {
  columns: DataTableColumn<ProjectType>[];
};

export const useProjectsTableColumns =
  (): UseProjectsTableColumnsReturnType => {
    const { t } = useTranslation('financeOverview');

    const noDataPlaceholder = t('NO_DATA_PLACEHOLDER', { ns: 'common' });

    const columns: DataTableColumn<ProjectType>[] = [
      {
        accessor: 'artist',
        sortable: true,
        width: 300,
        title: t(`${TRANS_PATH}.SHOW_NAME`),
        render: ({ show }) => <ShowTableCell show={show} />,
      },
      {
        accessor: 'showDate',
        sortable: true,
        width: 100,
        title: t(`${TRANS_PATH}.SHOW_DATE`),
        render: ({ show: { showDate } }) => formatDate(showDate),
      },
      { accessor: 'unit', title: t(`${TRANS_PATH}.UNIT`) },
      {
        accessor: 'projectNumber',
        sortable: true,
        title: t(`${TRANS_PATH}.PROJECT_NUMBER`),
      },
      {
        accessor: 'sellable',
        title: t(`${TRANS_PATH}.SELLABLE`),
        render: ({ sellable }) =>
          sellable ? formatNumber(sellable) : noDataPlaceholder,
      },
      {
        accessor: 'buyer.name',
        title: t(`${TRANS_PATH}.BUYER_NAME`),
        width: 200,
        render: ({ buyer }) =>
          buyer ? <PersonAnchor person={buyer} /> : noDataPlaceholder,
      },
      {
        accessor: 'agencyContact.name',
        title: t(`${TRANS_PATH}.AGENCY_CONTACT_NAME`),
        width: 200,
        render: ({ agencyContact }) =>
          agencyContact ? (
            <PersonAnchor person={agencyContact} />
          ) : (
            noDataPlaceholder
          ),
      },
      {
        accessor: 'arrangedBy',
        sortable: true,
        title: t(`${TRANS_PATH}.ARRANGED_BY_NAME`),
        width: 200,
        render: ({ arrangedBy }) =>
          arrangedBy ? (
            <CompanyAnchor company={arrangedBy} />
          ) : (
            noDataPlaceholder
          ),
      },
      {
        accessor: 'artistGuarantee',
        title: t(`${TRANS_PATH}.ARTIST_GUARANTEE`),
        render: ({ artistGuarantee }) =>
          artistGuarantee ? formatCurrency(artistGuarantee) : noDataPlaceholder,
      },
      {
        accessor: 'vsArtistPercent',
        title: t(`${TRANS_PATH}.VS_ARTIST_PERCENT`),
        render: ({ vsArtistPercent }) =>
          vsArtistPercent ? formatPercent(vsArtistPercent) : noDataPlaceholder,
      },
      {
        accessor: 'versusTicket',
        title: t(`${TRANS_PATH}.VERSUS_TICKET`),
        render: ({ vsTicketPercent }) =>
          vsTicketPercent ? formatPercent(vsTicketPercent) : noDataPlaceholder,
      },
      { accessor: 'dealStatus', title: t(`${TRANS_PATH}.DEAL_STATUS`) },
      {
        accessor: 'faceValue',
        title: t(`${TRANS_PATH}.FACE_VALUE`),
        render: ({ faceValue }) =>
          faceValue ? formatCurrency(faceValue) : noDataPlaceholder,
      },
      {
        accessor: 'coPro.name',
        title: t(`${TRANS_PATH}.CO_PRO_NAME`),
        width: 200,
        render: ({ coPro }) =>
          coPro ? <CompanyAnchor company={coPro} /> : noDataPlaceholder,
      },
      {
        accessor: 'deal',
        title: t(`${TRANS_PATH}.DEAL`),
        render: ({ deal }) => (deal ? deal : noDataPlaceholder),
      },

      {
        accessor: 'arrangement',
        title: t(`${TRANS_PATH}.ARRANGEMENT`),
        render: ({ arrangement }) =>
          arrangement ? (
            <Arrangement arrangement={arrangement} />
          ) : (
            noDataPlaceholder
          ),
      },
      {
        accessor: 'soldGage',
        title: t(`${TRANS_PATH}.SOLD_GAGE`),
        render: ({ soldGage }) =>
          soldGage ? formatCurrency(soldGage) : noDataPlaceholder,
      },
      {
        accessor: 'promotedGuarantee',
        title: t(`${TRANS_PATH}.PROMOTED_GUARANTEE`),
        render: ({ promotedGuarantee }) =>
          promotedGuarantee
            ? formatCurrency(promotedGuarantee)
            : noDataPlaceholder,
      },
      {
        accessor: 'profitShare',
        title: t(`${TRANS_PATH}.PROFIT_SHARE`),
        render: ({ profitShare }) =>
          profitShare ? formatPercent(profitShare) : noDataPlaceholder,
      },
      {
        accessor: 'lossShare',
        title: t(`${TRANS_PATH}.LOSS_SHARE`),
        render: ({ lossShare }) =>
          lossShare ? formatPercent(lossShare) : noDataPlaceholder,
      },
    ];

    return { columns };
  };
